import React, { useContext, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { AuthContext } from '../contexts/AuthContext';
import { AssistantPendingTransaction, AssistantResponse } from '../types';
import './AssistantWidget.css';

type WidgetMessage = {
  id: string;
  role: 'assistant' | 'user';
  content: string;
  action?: AssistantResponse['action'];
  pendingTransaction?: AssistantPendingTransaction;
};

type QuickAction = {
  id: string;
  label: string;
  kind: 'send' | 'prefill';
  value: string;
};

const AssistantWidget: React.FC = () => {
  const authContext = useContext(AuthContext);
  const { user } = authContext || {};
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [input, setInput] = useState<string>('');
  const [isSending, setIsSending] = useState<boolean>(false);
  const [messages, setMessages] = useState<WidgetMessage[]>([
    {
      id: 'assistant-welcome',
      role: 'assistant',
      content:
        'Oi! Sou seu assistente financeiro. Posso responder sobre saldo, comparativos, categorias, fontes e lançamentos do sistema.',
    },
  ]);

  const canUseAssistant = useMemo(
    () => user?.role === 'admin' || user?.role === 'manager' || user?.role === 'hr',
    [user],
  );

  const quickActions = useMemo<QuickAction[]>(
    () => [
      {
        id: 'expense',
        label: 'Cadastrar despesa',
        kind: 'prefill',
        value: 'despesa pix restaurante 31,34 hoje',
      },
      {
        id: 'income',
        label: 'Cadastrar receita',
        kind: 'prefill',
        value: 'receita banco aluguel 1500,00',
      },
      {
        id: 'balance',
        label: 'Ver saldo',
        kind: 'send',
        value: 'qual é o saldo atual?',
      },
      {
        id: 'comparison',
        label: 'Comparar período',
        kind: 'send',
        value: 'compare o mês atual com o anterior',
      },
      {
        id: 'reports',
        label: 'Abrir relatórios',
        kind: 'send',
        value: 'quero abrir os relatórios',
      },
    ],
    [],
  );

  const transactionExamples = useMemo(
    () => [
      'despesa pix restaurante 31,34 hoje',
      'despesa cartão gasolina 89,90 ontem',
      'receita banco aluguel 1500,00',
    ],
    [],
  );

  if (!canUseAssistant) {
    return null;
  }

  const clearPendingTransaction = (messageId: string): void => {
    setMessages((current) =>
      current.map((message) =>
        message.id === messageId ? { ...message, pendingTransaction: undefined } : message,
      ),
    );
  };

  const sendMessage = async (
    messageText: string,
    options?: {
      confirmation?: {
        intent: 'confirm_transaction';
        pendingTransaction: AssistantPendingTransaction;
      };
      hideUserMessage?: boolean;
    },
  ): Promise<void> => {
    const trimmedInput = messageText.trim();
    if (!trimmedInput || isSending) return;

    const userMessage: WidgetMessage | null = options?.hideUserMessage
      ? null
      : {
          id: `user-${Date.now()}`,
          role: 'user',
          content: trimmedInput,
        };

    if (userMessage) {
      setMessages((current) => [...current, userMessage]);
    }
    setInput('');
    setIsSending(true);

    try {
      const history = [...messages, ...(userMessage ? [userMessage] : [])].map((message) => ({
        role: message.role,
        content: message.content,
      }));

      const response = await api.post<AssistantResponse>('/assistant/chat', {
        message: trimmedInput,
        currentPath: location.pathname,
        history,
        confirmation: options?.confirmation,
      });

      setMessages((current) => [
        ...current,
        {
          id: `assistant-${Date.now()}`,
          role: 'assistant',
          content: response.data.answer,
          action: response.data.action,
          pendingTransaction: response.data.pendingTransaction,
        },
      ]);
    } catch (error) {
      console.error('Erro ao conversar com o assistente:', error);
      setMessages((current) => [
        ...current,
        {
          id: `assistant-error-${Date.now()}`,
          role: 'assistant',
          content: 'Não consegui responder agora. Tente novamente em instantes.',
        },
      ]);
    } finally {
      setIsSending(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    await sendMessage(input);
  };

  const handleConfirmTransaction = async (
    sourceMessageId: string,
    pendingTransaction: AssistantPendingTransaction,
  ): Promise<void> => {
    clearPendingTransaction(sourceMessageId);
    await sendMessage('Confirmar lançamento', {
      confirmation: {
        intent: 'confirm_transaction',
        pendingTransaction,
      },
    });
  };

  const handleCancelTransaction = (sourceMessageId: string): void => {
    clearPendingTransaction(sourceMessageId);
    setMessages((current) => [
      ...current,
      {
        id: `assistant-cancel-${Date.now()}`,
        role: 'assistant',
        content: 'Cadastro cancelado. Se quiser, ajuste o texto e envie novamente.',
      },
    ]);
  };

  const renderPendingTransactionPreview = (pendingTransaction: AssistantPendingTransaction) => {
    const { preview } = pendingTransaction;
    const badgeClassName =
      pendingTransaction.draft.type === 'expense'
        ? 'assistant-widget-preview-badge is-expense'
        : 'assistant-widget-preview-badge is-income';

    return (
      <div className="assistant-widget-preview-card">
        <div className="assistant-widget-preview-header">
          <span className={badgeClassName}>{preview.typeLabel}</span>
          <strong>{preview.amountFormatted}</strong>
        </div>

        <div className="assistant-widget-preview-grid">
          <div className="assistant-widget-preview-item">
            <span>Descrição</span>
            <strong>{preview.description}</strong>
          </div>
          <div className="assistant-widget-preview-item">
            <span>Data</span>
            <strong>{preview.dateLabel}</strong>
          </div>
          <div className="assistant-widget-preview-item">
            <span>Conta</span>
            <strong>{preview.accountLabel || 'Não identificada'}</strong>
          </div>
          <div className="assistant-widget-preview-item">
            <span>Categoria</span>
            <strong>{preview.categoryLabel || 'Automática/não encontrada'}</strong>
          </div>
          <div className="assistant-widget-preview-item assistant-widget-preview-item-full">
            <span>Fonte</span>
            <strong>{preview.sourceLabel || 'Automática/não encontrada'}</strong>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={`assistant-widget${isOpen ? ' is-open' : ''}`}>
      {isOpen && (
        <div className="assistant-widget-panel">
          <div className="assistant-widget-header">
            <div>
              <span className="assistant-widget-kicker">Assistente IA</span>
              <strong>Contexto da sua conta</strong>
            </div>
            <button
              type="button"
              className="assistant-widget-close"
              onClick={() => setIsOpen(false)}
              aria-label="Fechar assistente"
            >
              ×
            </button>
          </div>

          <div className="assistant-widget-shortcuts">
            <div className="assistant-widget-section">
              <span className="assistant-widget-section-title">Ações rápidas</span>
              <div className="assistant-widget-chip-list">
                {quickActions.map((action) => (
                  <button
                    key={action.id}
                    type="button"
                    className="assistant-widget-chip"
                    onClick={() => {
                      if (action.kind === 'prefill') {
                        setInput(action.value);
                        return;
                      }

                      void sendMessage(action.value);
                    }}
                    disabled={isSending}
                  >
                    {action.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="assistant-widget-section">
              <span className="assistant-widget-section-title">Formato rápido</span>
              <p className="assistant-widget-hint">
                Use: tipo + meio + categoria/descrição + valor + data opcional.
              </p>
              <div className="assistant-widget-example-list">
                {transactionExamples.map((example) => (
                  <button
                    key={example}
                    type="button"
                    className="assistant-widget-example"
                    onClick={() => setInput(example)}
                  >
                    {example}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="assistant-widget-messages">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`assistant-widget-message assistant-widget-message-${message.role}`}
              >
                <p>{message.content}</p>
                {message.pendingTransaction &&
                  renderPendingTransactionPreview(message.pendingTransaction)}
                {message.pendingTransaction && (
                  <div className="assistant-widget-confirmations">
                    <button
                      type="button"
                      className="assistant-widget-confirm"
                      onClick={() =>
                        void handleConfirmTransaction(message.id, message.pendingTransaction!)
                      }
                      disabled={isSending}
                    >
                      Confirmar
                    </button>
                    <button
                      type="button"
                      className="assistant-widget-cancel"
                      onClick={() => handleCancelTransaction(message.id)}
                      disabled={isSending}
                    >
                      Cancelar
                    </button>
                  </div>
                )}
                {message.action && (
                  <button
                    type="button"
                    className="assistant-widget-action"
                    onClick={() => {
                      if (message.action!.route === location.pathname) {
                        navigate(0);
                      } else {
                        navigate(message.action!.route);
                      }
                      setIsOpen(false);
                    }}
                  >
                    {message.action.label}
                  </button>
                )}
              </div>
            ))}
            {isSending && (
              <div className="assistant-widget-message assistant-widget-message-assistant">
                <p>Consultando os dados e preparando uma resposta...</p>
              </div>
            )}
          </div>

          <form className="assistant-widget-form" onSubmit={handleSubmit}>
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ex.: despesa pix restaurante 31,34 hoje"
              rows={3}
            />
            <button type="submit" disabled={isSending || !input.trim()}>
              Enviar
            </button>
          </form>
        </div>
      )}

      <button
        type="button"
        className="assistant-widget-trigger"
        onClick={() => setIsOpen((current) => !current)}
        aria-label="Abrir assistente"
      >
        IA
      </button>
    </div>
  );
};

export default AssistantWidget;
